<template>
  <v-container>
    <v-row>
      <v-col cols="9">
        <PhotoFilter :initial-filter="photoFilters" @apply-filters="setPhotoFilters($event)"></PhotoFilter>
      </v-col>
    </v-row>

    <!-- Photo Cards with Count Panels -->
    <v-row>
      <v-col
        v-for="photo in photos"
        :key="photo.id"
        :lg="6" :md="6" :sm="12" :xl="4" :xs="12"
      >
        <v-card class="mb-4">
          <v-card-title class="py-1">
            Response: {{ photo.response_id }}
            <v-chip v-if="photo.is_removed" color="error" class="ml-2" small>
              Removed
            </v-chip>
          </v-card-title>
          <image-zoom
              class="mb-3"
              :regular="photo.image_url"
              :zoom="photo.image_url"
              :zoom-amount="4"
              :click-zoom="true"
              img-class="count-image"
              :style="photo.is_removed ? 'opacity: 0.5' : ''"
            />
          <v-card-text>
            <CountPanel
              :ref="`countPanel_${photo.id}`"
              :photo="photo"
              :isQc="false"
              @saved="onCountSaved"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-container class="fixed border--primary">
      <v-btn
        class="mr-2 float-right"
        color="success"
        @click="submitBatch({getNext: true})"
      >
        Submit & Get Next Batch
      </v-btn>
      <v-btn
        class="mr-2 float-right"
        color="primary"
        @click="submitBatch({getNext: false})"
      >
        Submit & Close
      </v-btn>
    </v-container>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import PhotoFilter from '@/components/filters/PhotoFilter'
import {routeFilterSeed} from '@/components/filters/routeSeed'
import CountPanel from '@/components/counts/CountPanel'
import imageZoom from 'vue-image-zoomer'

export default {
  name: 'PhotoCounting',
  components: {
    PhotoFilter,
    CountPanel,
    imageZoom
  },
  data() {
    return {
      photoFilters: {
        fromDt: null,
        thruDt: null,
        tags: [],
        projects: [],
        jobs: [],
        onlyUntagged: true,
        includeInactive: false,
        respIdSearchFreeForm: null,
        country: null,
        retailer: null,
        department: null
      }
    }
  },
  // Quick-link deep links (routeSeed.js) must seed the filter in created(),
  // BEFORE the filter child mounts: Vue 2 mounts the child before the parent,
  // so seeding in the parent's mounted() lands after the child has snapshotted
  // initialFilter — leaving the Applied-Filters chips blank and dropping the
  // scope on the next Apply. created() runs first, so the child snapshots the
  // seeded filter. Matches BoxTagging's timing.
  created() {
    this.photoFilters = {...this.photoFilters, ...routeFilterSeed(this.$route)}
  },
  mounted() {
    this.getCountItems()
    this.getPhotosByFilter(this.photoFilters)
  },
  computed: {
    ...mapState({
      photos: state => state.photos.photos,
    })
  },
  methods: {
    ...mapActions(['getPhotosByFilter', 'submitAssigned', 'getCountItems']),
    ...mapMutations(['setMessage', 'setPhotos']),

    setPhotoFilters(newFilter) {
      this.photoFilters = newFilter
      this.getPhotosByFilter(this.photoFilters)
    },
    onCountSaved(photo) {
      const text = photo.is_removed
        ? `Display removed for photo ${photo.response_id}`
        : `Counts saved for photo ${photo.response_id}`
      this.setMessage({ text, type: 'success', alert: true })
    },
    collectPanels() {
      return this.photos
        .map(p => this.$refs[`countPanel_${p.id}`])
        .map(ref => Array.isArray(ref) ? ref[0] : ref)
        .filter(Boolean)
    },
    async saveAllDirty() {
      const panels = this.collectPanels()
      const results = await Promise.allSettled(panels.map(panel => panel.saveIfDirty()))
      return results.filter(r => r.status === 'rejected').map(r => r.reason)
    },
    async submitBatch(payload) {
      const failures = await this.saveAllDirty()
      if (failures.length > 0) {
        const ids = failures.map(f => f.responseId || f.photoId).join(', ')
        this.setMessage({
          text: `Could not save ${failures.length} photo(s): ${ids}. Fix or cancel changes, then resubmit.`,
          type: 'error',
          alert: true
        })
        return
      }
      this.submitAssigned().then(() => {
        this.setMessage({ text: 'Photos Submitted', type: 'success', alert: true })
        this.setPhotos([])
        if (payload.getNext) {
          this.getPhotosByFilter(this.photoFilters)
        }
      })
    }
  }
}
</script>

<style scoped>
.border--primary {
  border: 1px solid #666;
  border-radius: 4px;
  padding: 10px;
}
.fixed {
  position: fixed !important;
  bottom: 0 !important;
  z-index: 999;
  background: white;
}
</style>

<style>
@media (min-width: 960px) {
  .count-image {
    max-width: 400px;
  }
}
@media (min-width: 1264px) {
  .count-image {
    max-width: 530px;
  }
}
@media (min-width: 1904px) {
  .count-image {
    max-width: 540px;
  }
}
</style>
